import { semicirclesToDegrees } from '@fit-editor/fit-core'
import type { ActivityRecord } from '@fit-editor/fit-core'

/**
 * GPS-related pure helpers shared by the map component. Kept dependency-free
 * from MapLibre/React so they're trivially unit-testable under plain
 * Vitest (no jsdom/WebGL needed).
 */

export interface GpsPoint {
  /** Index into the activity's `records` array this point came from. */
  recordIndex: number
  lon: number
  lat: number
  /** Epoch ms, or `null` if this record has no timestamp. */
  timestampMs: number | null
}

function hasPosition(
  r: ActivityRecord,
): r is ActivityRecord & { positionLat: number; positionLong: number } {
  return typeof r.positionLat === 'number' && typeof r.positionLong === 'number'
}

/** True if at least one record has a GPS position, i.e. `extractGpsPoints` would return a point (early exit, no allocation). */
export function hasGpsData(records: readonly ActivityRecord[]): boolean {
  return records.some(hasPosition)
}

/** Records without a GPS position are skipped entirely, never plotted at the origin. */
export function extractGpsPoints(records: ActivityRecord[]): GpsPoint[] {
  const points: GpsPoint[] = []
  for (let i = 0; i < records.length; i++) {
    const record = records[i]
    if (record == null || !hasPosition(record)) continue
    points.push({
      recordIndex: i,
      lon: semicirclesToDegrees(record.positionLong),
      lat: semicirclesToDegrees(record.positionLat),
      timestampMs: record.timestamp instanceof Date ? record.timestamp.getTime() : null,
    })
  }
  return points
}

export type LngLatBoundsTuple = [[number, number], [number, number]]

export function computeLngLatBounds(points: GpsPoint[]): LngLatBoundsTuple | null {
  if (points.length === 0) return null
  let minLon = Infinity
  let minLat = Infinity
  let maxLon = -Infinity
  let maxLat = -Infinity
  for (const point of points) {
    if (point.lon < minLon) minLon = point.lon
    if (point.lon > maxLon) maxLon = point.lon
    if (point.lat < minLat) minLat = point.lat
    if (point.lat > maxLat) maxLat = point.lat
  }
  return [
    [minLon, minLat],
    [maxLon, maxLat],
  ]
}

/**
 * The GPS point nearest a target instant (epoch ms), among points that have
 * a timestamp. Linear scan -- FIT files have at most a few tens of
 * thousands of records, so this is microseconds even unoptimized, and it's
 * only called from throttled hover handlers, never a tight render loop.
 */
export function nearestGpsPointByTime(points: GpsPoint[], targetMs: number): GpsPoint | undefined {
  let best: GpsPoint | undefined
  let bestDelta = Infinity
  for (const point of points) {
    if (point.timestampMs == null) continue
    const delta = Math.abs(point.timestampMs - targetMs)
    if (delta < bestDelta) {
      bestDelta = delta
      best = point
    }
  }
  return best
}

/** GPS points (in original record order) whose timestamp falls within `[startMs, endMs]`. */
export function gpsPointsInRange(points: GpsPoint[], startMs: number, endMs: number): GpsPoint[] {
  return points.filter(
    (point) =>
      point.timestampMs != null && point.timestampMs >= startMs && point.timestampMs <= endMs,
  )
}
